import React, { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Mail, 
  Send, 
  Inbox, 
  FileText, 
  Search, 
  RefreshCw, 
  Trash2, 
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Plus,
  User,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Link } from 'react-router-dom';

const MessagesPage = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [folder, setFolder] = useState('sent');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // View message modal
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  
  // Compose modal
  const [composeOpen, setComposeOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState({
    to_email: '',
    to_name: '',
    subject: '',
    body: '',
    message_type: 'manual'
  });

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        folder,
        page: page.toString(),
        per_page: '20'
      });
      if (search) params.append('search', search);
      
      const response = await api.get(`/admin/messages?${params.toString()}`);
      const data = await response.json();
      
      setMessages(data.messages || []);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Erreur lors du chargement des messages');
    } finally {
      setLoading(false);
    }
  }, [folder, page, search]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/messages/stats');
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchMessages();
    fetchStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMessages]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchMessages();
  };

  const viewMessage = async (messageId) => {
    try {
      const response = await api.get(`/admin/messages/${messageId}`);
      const data = await response.json();
      setSelectedMessage(data);
      setViewModalOpen(true);
    } catch (error) {
      toast.error('Erreur lors du chargement du message');
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    
    try {
      await api.delete(`/admin/messages/${messageId}`);
      toast.success('Message supprimé');
      fetchMessages();
      fetchStats();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const sendMessage = async () => {
    if (!newMessage.to_email || !newMessage.subject || !newMessage.body) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setSending(true);
    try {
      const response = await api.post('/admin/messages', newMessage);
      const data = await response.json();
      
      if (data.success) {
        toast.success('Message envoyé');
        setComposeOpen(false);
        setNewMessage({ to_email: '', to_name: '', subject: '', body: '', message_type: 'manual' });
        fetchMessages();
        fetchStats();
      } else {
        toast.error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status, hasError) => {
    if (hasError || status === 'failed') {
      return <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" /> Échec</Badge>;
    }
    switch (status) {
      case 'sent':
        return <Badge className="bg-tp-accent/10 text-tp-accent gap-1"><CheckCircle className="h-3 w-3" /> Envoyé</Badge>;
      case 'received':
        return <Badge className="bg-tp-primary/10 text-tp-primary gap-1"><Inbox className="h-3 w-3" /> Reçu</Badge>;
      case 'queued':
      case 'pending':
        return <Badge className="bg-tp-primary/10 text-tp-primary gap-1"><Clock className="h-3 w-3" /> En file</Badge>;
      case 'draft':
        return <Badge variant="secondary" className="gap-1"><FileText className="h-3 w-3" /> Brouillon</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getTypeBadge = (type) => {
    const types = {
      'manual': { label: 'Manuel', color: 'bg-tp-primary/10 text-tp-primary' },
      'notification': { label: 'Notification', color: 'bg-tp-primary/10 text-tp-primary' },
      'qualification': { label: 'Qualification', color: 'bg-tp-accent/10 text-tp-accent' },
      'confirmation': { label: 'Confirmation', color: 'bg-tp-accent/10 text-tp-accent' },
      'qualification_form': { label: 'Formulaire', color: 'bg-tp-accent/10 text-tp-accent' },
      'candidate_rejection': { label: 'Rejet', color: 'bg-red-50 text-red-600' },
      'client_candidate_proposal': { label: 'Proposition client', color: 'bg-tp-primary/10 text-tp-primary' },
      'evaluation_form_sent': { label: 'Évaluation', color: 'bg-tp-accent/10 text-tp-accent' },
      'meeting_invitation': { label: 'Entretien', color: 'bg-tp-primary/10 text-tp-primary' },
      'received': { label: 'Reçu', color: 'bg-tp-accent/10 text-tp-accent' },
      'system': { label: 'Système', color: 'bg-gray-100 text-gray-600' }
    };
    const config = types[type] || { label: type, color: 'bg-gray-100 text-gray-600' };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="messages-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-display font-bold text-tp-text flex items-center gap-2">
            <Mail className="h-6 w-6 text-tp-primary" />
            Messagerie
          </h1>
          <p className="text-tp-text-light mt-1 text-sm">
            Historique des emails envoyés et reçus
          </p>
        </div>
        <Button 
          onClick={() => setComposeOpen(true)}
          className="bg-tp-primary hover:bg-tp-primary-light text-white gap-2"
          data-testid="compose-button"
        >
          <Plus className="h-4 w-4" />
          Nouveau message
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 border-tp-border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Send className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-tp-text">{stats.total_sent}</p>
                <p className="text-xs text-tp-text-light">Envoyés</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 border-tp-border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-tp-text">{stats.total_failed}</p>
                <p className="text-xs text-tp-text-light">Échecs</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 border-tp-border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <FileText className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-tp-text">{stats.total_drafts}</p>
                <p className="text-xs text-tp-text-light">Brouillons</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 border-tp-border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-tp-primary/10 rounded-lg">
                <Clock className="h-5 w-5 text-tp-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-tp-text">{stats.sent_last_7_days}</p>
                <p className="text-xs text-tp-text-light">7 derniers jours</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="p-4 border-tp-border">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Folder Tabs */}
          <div className="flex gap-2">
            <Button
              variant={folder === 'sent' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setFolder('sent'); setPage(1); }}
              className={folder === 'sent' ? 'bg-tp-primary' : ''}
            >
              <Send className="h-4 w-4 mr-1" />
              Envoyés
            </Button>
            <Button
              variant={folder === 'received' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setFolder('received'); setPage(1); }}
              className={folder === 'received' ? 'bg-tp-primary' : ''}
              data-testid="folder-received"
            >
              <Inbox className="h-4 w-4 mr-1" />
              Reçus
            </Button>
            <Button
              variant={folder === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setFolder('all'); setPage(1); }}
              className={folder === 'all' ? 'bg-tp-primary' : ''}
            >
              <Mail className="h-4 w-4 mr-1" />
              Tous
            </Button>
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-tp-text-light" />
              <Input
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button type="submit" variant="outline" size="icon">
              <Search className="h-4 w-4" />
            </Button>
            <Button type="button" variant="outline" size="icon" onClick={fetchMessages} title="Rafraîchir">
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              size="sm"
              onClick={async () => {
                toast.info('Récupération des emails...');
                try {
                  const res = await api.post('/admin/messages/fetch-inbox');
                  if (res.ok) {
                    const data = await res.json();
                    toast.success(data.message);
                    fetchMessages();
                  } else {
                    const err = await res.json();
                    toast.error(err.detail || 'Erreur IMAP');
                  }
                } catch (e) { toast.error('Erreur de connexion'); }
              }}
              data-testid="fetch-inbox-btn"
              title="Récupérer les emails reçus"
            >
              <Inbox className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Récupérer</span>
            </Button>
          </form>
        </div>
      </Card>

      {/* Messages List */}
      <Card className="border-tp-border">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-tp-primary" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="h-12 w-12 text-tp-text-light mx-auto mb-4" />
              <p className="text-tp-text-light">Aucun message trouvé</p>
            </div>
          ) : (
            <div className="divide-y divide-tp-border">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => viewMessage(message.id)}
                  data-testid={`message-item-${message.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-medium text-tp-text truncate">
                          {message.to_name || message.to_email}
                        </span>
                        {getStatusBadge(message.status, message.has_error)}
                        {getTypeBadge(message.message_type)}
                      </div>
                      <p className="text-sm font-medium text-tp-text truncate">{message.subject}</p>
                      <p className="text-sm text-tp-text-light truncate mt-1">{message.body_preview}</p>
                      
                      {/* Related links */}
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs text-tp-text-light">{formatDate(message.sent_at || message.created_at)}</span>
                        {message.related_candidate_id && (
                          <Link 
                            to={`/admin/candidates/${message.related_candidate_id}`}
                            className="text-xs text-tp-primary hover:underline flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <User className="h-3 w-3" />
                            Voir candidat
                          </Link>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); viewMessage(message.id); }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); deleteMessage(message.id); }}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-tp-border">
              <p className="text-sm text-tp-text-light">
                {total} message{total > 1 ? 's' : ''} au total
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-tp-text">
                  Page {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Message Modal */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              {selectedMessage?.subject}
            </DialogTitle>
          </DialogHeader>
          
          {selectedMessage && (
            <div className="space-y-4">
              {/* Meta info */}
              <div className="flex flex-wrap gap-2">
                {getStatusBadge(selectedMessage.status)}
                {getTypeBadge(selectedMessage.message_type)}
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-tp-text-light">De</p>
                  <p className="font-medium">{selectedMessage.from_name} &lt;{selectedMessage.from_email}&gt;</p>
                </div>
                <div>
                  <p className="text-tp-text-light">À</p>
                  <p className="font-medium">{selectedMessage.to_name || selectedMessage.to_email}</p>
                  <p className="text-xs text-tp-text-light">{selectedMessage.to_email}</p>
                </div>
                <div>
                  <p className="text-tp-text-light">Date</p>
                  <p className="font-medium">{formatDate(selectedMessage.sent_at || selectedMessage.created_at)}</p>
                </div>
                {selectedMessage.related_candidate_id && (
                  <div>
                    <p className="text-tp-text-light">Candidat lié</p>
                    <Link 
                      to={`/admin/candidates/${selectedMessage.related_candidate_id}`}
                      className="text-tp-primary hover:underline flex items-center gap-1"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Voir le profil
                    </Link>
                  </div>
                )}
              </div>

              {/* Error message */}
              {selectedMessage.error_message && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-700">
                    <AlertCircle className="h-4 w-4" />
                    <span className="font-medium">Erreur d'envoi</span>
                  </div>
                  <p className="text-sm text-red-600 mt-1">{selectedMessage.error_message}</p>
                </div>
              )}

              {/* Body */}
              <div className="border border-tp-border rounded-lg p-4 bg-gray-50">
                {selectedMessage.html_body ? (
                  <div 
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedMessage.html_body) }}
                  />
                ) : (
                  <pre className="whitespace-pre-wrap text-sm font-sans">{selectedMessage.body}</pre>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Compose Modal */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Nouveau message
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email destinataire *</Label>
                <Input
                  type="email"
                  placeholder="email@exemple.com"
                  value={newMessage.to_email}
                  onChange={(e) => setNewMessage({...newMessage, to_email: e.target.value})}
                />
              </div>
              <div>
                <Label>Nom (optionnel)</Label>
                <Input
                  placeholder="Nom du destinataire"
                  value={newMessage.to_name}
                  onChange={(e) => setNewMessage({...newMessage, to_name: e.target.value})}
                />
              </div>
            </div>
            
            <div>
              <Label>Sujet *</Label>
              <Input
                placeholder="Objet du message"
                value={newMessage.subject}
                onChange={(e) => setNewMessage({...newMessage, subject: e.target.value})}
              />
            </div>
            
            <div>
              <Label>Message *</Label>
              <Textarea
                placeholder="Votre message..."
                rows={8}
                value={newMessage.body}
                onChange={(e) => setNewMessage({...newMessage, body: e.target.value})}
              />
            </div>
            
            <div>
              <Label>Type de message</Label>
              <Select 
                value={newMessage.message_type} 
                onValueChange={(v) => setNewMessage({...newMessage, message_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manuel</SelectItem>
                  <SelectItem value="notification">Notification</SelectItem>
                  <SelectItem value="qualification">Qualification</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setComposeOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={sendMessage} 
              disabled={sending}
              className="bg-tp-primary hover:bg-tp-primary-light"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MessagesPage;
